import { Box } from "@mui/material";
import { createRootRoute, createRoute, createRouter, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Shell, TenantPage, withTenant } from "../chrome/Shell";
import { Account } from "../pages/account/Account";
import { Reset } from "../pages/authn/Reset";
import { TokenInspector } from "../pages/developer/TokenInspector";
import { type Page as PageName, type TenantPageProps, firstOpenPage } from "../pages/pages";
import { Platform } from "../pages/platform/Platform";
import { Apis } from "../pages/tenant/Apis";
import { Applications } from "../pages/tenant/Applications";
import { Audit } from "../pages/tenant/AuditLog";
import { Bulk } from "../pages/tenant/Bulk";
import { Claims } from "../pages/tenant/Claims";
import { EmailTemplates } from "../pages/tenant/EmailTemplates";
import { LandingPages } from "../pages/tenant/LandingPages";
import { Members } from "../pages/tenant/Members";
import { NewUser } from "../pages/tenant/NewUser";
import { Projects } from "../pages/tenant/Projects";
import { Roles } from "../pages/tenant/Roles";
import { useConsole } from "../state/state";

// ---
// Where the console can be
//
// A route is a PLACE. Everything that answers "what am I looking at" lives in the URL rather than
// in component state, which is what makes a page linkable, reachable with the back button, and
// still there after a reload. An audit entry about a role can point at that role.
//
// The tenant is a path segment because it is part of the place, not a filter on it: `/users` is
// ambiguous across tenants and `/tenants/shell/users` is not. Switching tenants is navigation.
//
// `/?invitation=…` stays a search parameter on the index rather than becoming a route. Links of
// that shape are already in people's mail, and a router that broke them would be a worse answer
// than a slightly irregular URL.
// ---

const root = createRootRoute({ component: Shell });

/** `/` — nothing names a tenant yet, so stand in for the first one the subject belongs to. */
const index = createRoute({
  getParentRoute: () => root,
  path: "/",
  // The redirect happens in the component, not `beforeLoad`: which tenant to choose is known
  // only once the session has loaded, and `beforeLoad` runs before any of that exists.
  component: TenantPage,
});

/**
 * The password-reset link's landing place, before any session.
 *
 * Its token, error and tenant arrive as search parameters, which is what the mailed link carries.
 *
 * There is deliberately no `continue` among them. Nothing server-side ever set one, so it could
 * only have come from whoever opened the link — and a page that forwards to an address its opener
 * chose is an open redirect wearing our domain.
 */
const reset = createRoute({
  getParentRoute: () => root,
  path: "/reset-password",
  component: function ResetPage() {
    const params = new URLSearchParams(window.location.search);
    return (
      <Reset
        token={params.get("token")}
        error={params.get("error")}
        tenant={params.get("tenant")}
        onDone={() => {
          window.location.assign("/");
        }}
      />
    );
  },
});

/** Across tenants. Its own place because it is not inside any one of them. */
const platform = createRoute({
  getParentRoute: () => root,
  path: "/platform",
  component: function PlatformPage() {
    const { platformToken, user } = useConsole();
    return <Platform token={platformToken} subjectId={user?.id ?? ""} />;
  },
});

const tokens = createRoute({
  getParentRoute: () => root,
  path: "/tokens",
  component: TokenInspector,
});

const account = createRoute({
  getParentRoute: () => root,
  path: "/account",
  component: function AccountPage() {
    const { user, tenants, controlScopes } = useConsole();
    if (user === null) return <></>;
    return <Account user={user} tenants={[...tenants]} scopes={[...controlScopes]} />;
  },
});

/** `/tenants/:tenant` — everything below operates inside the one the path names. */
const tenant = createRoute({
  getParentRoute: () => root,
  path: "/tenants/$tenant",
});

/**
 * Every tenant page, by name, so the redirect below keeps the router's checking.
 *
 * A template string would be shorter and would give up exactly what `page()` below is written out
 * to preserve: `to` is a literal union, and a computed path is checked by nobody.
 */
const HOME = {
  apis: "/tenants/$tenant/apis",
  applications: "/tenants/$tenant/applications",
  projects: "/tenants/$tenant/projects",
  users: "/tenants/$tenant/users",
  "users-new": "/tenants/$tenant/users-new",
  roles: "/tenants/$tenant/roles",
  bulk: "/tenants/$tenant/bulk",
  claims: "/tenants/$tenant/claims",
  email: "/tenants/$tenant/email",
  landing: "/tenants/$tenant/landing",
  logs: "/tenants/$tenant/logs",
  platform: "/platform",
  tokens: "/tokens",
} as const;

/**
 * `/tenants/:tenant` — stands in for the first page this caller may open.
 *
 * A component rather than `beforeLoad`, for the same reason the index above is one: which page
 * that is depends on the control-plane token for this tenant, and `beforeLoad` runs before there
 * is one.
 */
const tenantIndex = createRoute({
  getParentRoute: () => tenant,
  path: "/",
  component: function TenantHome(): React.JSX.Element {
    const navigate = useNavigate();
    const { tenant: current, controlScopes, controlLoaded, loading } = useConsole();
    useEffect(() => {
      // Not until the token attempt has settled: with no scopes yet, every page looks shut and
      // the fallback sends somebody to the one page they are most likely to be refused.
      if (loading || current === "" || !controlLoaded) return;
      void navigate({
        to: HOME[firstOpenPage(controlScopes)],
        params: { tenant: current },
        replace: true,
      });
    }, [loading, current, controlScopes, controlLoaded, navigate]);
    return <Box />;
  },
});

/**
 * One route per page, written out rather than generated.
 *
 * A `.map()` over a table would be shorter and would throw away what makes this worth doing:
 * the router infers its paths from these declarations, so `/tenants/$tenant/users` is checked at
 * every call site. Built dynamically, every `navigate` and `Link` becomes a string again.
 */
function page(id: PageName, component: (props: TenantPageProps) => React.JSX.Element) {
  return { path: id, component: withTenant(id, component) };
}

const applications = createRoute({
  getParentRoute: () => tenant,
  ...page("applications", Applications),
});
const apisPage = createRoute({ getParentRoute: () => tenant, ...page("apis", Apis) });
const users = createRoute({ getParentRoute: () => tenant, ...page("users", Members) });
const newUser = createRoute({ getParentRoute: () => tenant, ...page("users-new", NewUser) });
const roles = createRoute({ getParentRoute: () => tenant, ...page("roles", Roles) });
const bulk = createRoute({ getParentRoute: () => tenant, ...page("bulk", Bulk) });
const projects = createRoute({ getParentRoute: () => tenant, ...page("projects", Projects) });
const claims = createRoute({ getParentRoute: () => tenant, ...page("claims", Claims) });
const email = createRoute({
  getParentRoute: () => tenant,
  ...page("email", EmailTemplates),
});
const landing = createRoute({
  getParentRoute: () => tenant,
  ...page("landing", LandingPages),
});
const logs = createRoute({ getParentRoute: () => tenant, ...page("logs", Audit) });

export const routeTree = root.addChildren([
  index,
  reset,
  platform,
  tokens,
  account,
  tenant.addChildren([
    tenantIndex,
    applications,
    apisPage,
    users,
    newUser,
    roles,
    bulk,
    projects,
    claims,
    email,
    landing,
    logs,
  ]),
]);

export type ConsoleRouter = ReturnType<typeof makeRouter>;

export function makeRouter() {
  // No `declare module Register`: registering the router globally makes every `to` in this file
  // check against a type inferred from the tree being declared, and the tenant's children are
  // not known yet when `tenantIndex` redirects into them.
  return createRouter({ routeTree, defaultPreload: false });
}
